import { API } from '../../api/Api';
import { getPublicSubathon } from '../../api/payment/subathon.api';
import { parseServerDate } from '../../utils/server-date';
import { maskEmailLike } from '../../utils/honor-rows';

/**
 * Bảng xếp hạng donate trên live (overlay OBS) và bản xem trước trong cài đặt.
 *
 * Mọi tuỳ chọn nằm trên link overlay (?range=&by=&top=&style=), không lưu ở máy chủ: streamer
 * chép link nào thì OBS hiện đúng kiểu đó, đổi kiểu thì chép link mới. Giá trị lạ trên link
 * rơi về mặc định để overlay đang chạy trên live không bao giờ vỡ.
 */

export type TopDonateRange = 'today' | 'week' | 'month' | 'all' | 'subathon';
export type TopDonateBy = 'money' | 'count';
export type TopDonateStyle = 'dark' | 'light' | 'clear';

export interface TopDonateOptions {
  range: TopDonateRange;
  by: TopDonateBy;
  top: number;
  style: TopDonateStyle;
}

export interface TopDonateRow {
  userId: string;
  name: string;
  value: number;
}

export const TOP_DONATE_RANGES: Array<{ id: TopDonateRange; label: string; title: string }> = [
  { id: 'today', label: 'Hôm nay', title: 'Top donate hôm nay' },
  { id: 'week', label: 'Tuần này', title: 'Top donate tuần này' },
  { id: 'month', label: 'Tháng này', title: 'Top donate tháng này' },
  { id: 'subathon', label: 'Từ lúc bắt đầu subathon', title: 'Top donate subathon' },
  { id: 'all', label: 'Từ trước tới nay', title: 'Top donate' },
];

export const TOP_DONATE_BY: Array<{ id: TopDonateBy; label: string }> = [
  { id: 'money', label: 'Theo tổng số Ca' },
  { id: 'count', label: 'Theo số lượt donate' },
];

export const TOP_DONATE_STYLES: Array<{ id: TopDonateStyle; label: string }> = [
  { id: 'dark', label: 'Nền tối' },
  { id: 'light', label: 'Nền sáng' },
  { id: 'clear', label: 'Trong suốt' },
];

export const TOP_DONATE_MIN = 3;
export const TOP_DONATE_MAX = 10;

export const DEFAULT_TOP_DONATE_OPTIONS: TopDonateOptions = {
  range: 'today',
  by: 'money',
  top: 5,
  style: 'dark',
};

const pick = <T extends string>(value: string | null, allowed: Array<{ id: T }>, fallback: T): T =>
  allowed.some((x) => x.id === value) ? (value as T) : fallback;

export const parseTopDonateOptions = (params: URLSearchParams): TopDonateOptions => {
  const d = DEFAULT_TOP_DONATE_OPTIONS;
  const top = Number.parseInt(params.get('top') || '', 10);
  return {
    range: pick(params.get('range'), TOP_DONATE_RANGES, d.range),
    by: pick(params.get('by'), TOP_DONATE_BY, d.by),
    top: Number.isFinite(top) ? Math.min(TOP_DONATE_MAX, Math.max(TOP_DONATE_MIN, top)) : d.top,
    style: pick(params.get('style'), TOP_DONATE_STYLES, d.style),
  };
};

export const topDonateQuery = (o: TopDonateOptions): string =>
  new URLSearchParams({ range: o.range, by: o.by, top: `${o.top}`, style: o.style }).toString();

export const topDonateTitle = (range: TopDonateRange): string =>
  TOP_DONATE_RANGES.find((x) => x.id === range)?.title || 'Top donate';

// Mốc ngày/tuần/tháng tính theo giờ Việt Nam (UTC+7, không đổi giờ theo mùa), không theo giờ
// máy đang chạy OBS, để streamer ở đâu thì "hôm nay" vẫn là hôm nay của người xem Cab.
const VN_OFFSET_MS = 7 * 3600 * 1000;
const DAY_MS = 24 * 3600 * 1000;

/** Mốc bắt đầu tính của khoảng đã chọn; null là từ trước tới nay. */
export const topDonateRangeStart = (
  range: TopDonateRange,
  now: Date,
  subathonStartedAt?: Date | null
): Date | null => {
  const vn = new Date(now.getTime() + VN_OFFSET_MS);
  const y = vn.getUTCFullYear();
  const m = vn.getUTCMonth();
  const startOfToday = Date.UTC(y, m, vn.getUTCDate()) - VN_OFFSET_MS;
  switch (range) {
    case 'all':
      return null;
    case 'week': {
      const daysSinceMonday = (vn.getUTCDay() + 6) % 7;
      return new Date(startOfToday - daysSinceMonday * DAY_MS);
    }
    case 'month':
      return new Date(Date.UTC(y, m, 1) - VN_OFFSET_MS);
    case 'subathon':
      // Chưa từng chạy subathon thì tính như hôm nay, bảng không bị trống vô cớ.
      return subathonStartedAt || new Date(startOfToday);
    default:
      return new Date(startOfToday);
  }
};

const displayName = (item: any): string => {
  const full = typeof item?.fullName === 'string' ? item.fullName.trim() : '';
  return maskEmailLike(full || item?.userName || '');
};

/**
 * Lấy bảng xếp hạng theo tuỳ chọn. Không cần đăng nhập (overlay OBS chạy không có vé).
 * Người donate ẩn danh không có trong bảng (máy chủ đã loại).
 */
export const loadTopDonate = async (
  userId: string,
  options: TopDonateOptions,
  now: Date = new Date()
): Promise<TopDonateRow[]> => {
  let subathonStartedAt: Date | null = null;
  let to: Date | null = null;
  if (options.range === 'subathon') {
    try {
      const sub = await getPublicSubathon(userId);
      if (sub?.exists) {
        subathonStartedAt = parseServerDate(sub.startedAt);
        // Subathon đã kết thúc: bảng dừng ở lúc kết thúc, không cộng tiếp các lượt donate sau đó.
        if (subathonStartedAt && sub.status === 'Ended') to = parseServerDate(sub.endedAt);
      }
    } catch {
      // Không đọc được subathon: tính như hôm nay.
    }
  }
  const from = topDonateRangeStart(options.range, now, subathonStartedAt);
  const res = await API.get('/v1/payment-service/public/creator-top-donators', {
    params: {
      userId,
      top: options.top,
      ...(from ? { from: from.toISOString() } : {}),
      ...(to ? { to: to.toISOString() } : {}),
    },
  });
  const list: any[] = (options.by === 'count' ? res.data?.topAction : res.data?.topMoney) || [];
  return list.slice(0, options.top).map((item) => ({
    userId: `${item?.userId || ''}`,
    name: displayName(item),
    value: Number(options.by === 'count' ? item?.totalAction : item?.totalAmount) || 0,
  }));
};
